import { useState } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import TranslateIcon from '@mui/icons-material/Translate';

import { BTN_COLOR, THEME_COLOR3, THEME_COLOR4 } from '../../../../../constants';
import type { TMenuItem, TRestaurantLocalizationConfig } from '../schemas';
import MenuTranslationDialog from './MenuTranslationDialog';

type TTranslationCardProps = {
  itemData: TMenuItem | null;
  restaurantId: string;
  localizationConfig: TRestaurantLocalizationConfig | undefined;
};

const TranslationCard = ({ itemData, restaurantId, localizationConfig }: TTranslationCardProps) => {
  const [dialogOpen, setDialogOpen] = useState(false);

  const isEnabled = localizationConfig?.isEnabled ?? false;
  const configuredLanguages = localizationConfig?.languages ?? [];
  const canOpen = !!itemData?._id && isEnabled && configuredLanguages.length > 0;

  const statusMessage = !itemData
    ? 'Save the item first to add translations'
    : !isEnabled
      ? 'Enable multi-language in Menu Management'
      : configuredLanguages.length === 0
        ? 'Configure languages in Menu Management'
        : `${configuredLanguages.length} language${configuredLanguages.length !== 1 ? 's' : ''} configured`;

  return (
    <>
      <Box sx={styles.card}>
        <Box sx={styles.header}>
          <TranslateIcon sx={{ fontSize: 18, color: THEME_COLOR3 }} />
          <Typography sx={styles.title}>Translations</Typography>
          {isEnabled && configuredLanguages.length > 0 && (
            <Box sx={styles.badge}>{configuredLanguages.length}</Box>
          )}
        </Box>

        <Box sx={styles.body}>
          <Typography sx={styles.desc}>{statusMessage}</Typography>
          <Button
            type="button"
            variant="outlined"
            size="small"
            fullWidth
            disabled={!canOpen}
            onClick={() => setDialogOpen(true)}
            sx={styles.openBtn}
          >
            Open Translation Form
          </Button>
        </Box>
      </Box>

      {canOpen && (
        <MenuTranslationDialog
          open={dialogOpen}
          onClose={() => setDialogOpen(false)}
          restaurantId={restaurantId}
          menuItemId={itemData._id}
          configuredLanguages={configuredLanguages}
        />
      )}
    </>
  );
};

export default TranslationCard;

const styles = {
  card: {
    borderRadius: '12px',
    border: `1px solid ${THEME_COLOR4}`,
    backgroundColor: '#FFF',
    overflow: 'hidden',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    gap: 1,
    px: 2,
    py: 1.5,
    borderBottom: `1px solid ${THEME_COLOR4}`,
  },
  title: { fontFamily: 'GilroySemiBold', fontSize: 14, color: '#000', flex: 1 },
  badge: {
    backgroundColor: `${BTN_COLOR}1A`,
    color: BTN_COLOR,
    borderRadius: '999px',
    px: 1,
    py: 0.25,
    fontSize: 12,
    fontFamily: 'GilroySemiBold',
  },
  body: { px: 2, pt: 1.5, pb: 2 },
  desc: { fontSize: 12, fontFamily: 'GilroyRegular', color: THEME_COLOR3, mb: 1.5 },
  openBtn: {
    width: '100%',
    textTransform: 'none',
    fontFamily: 'GilroySemiBold',
    fontSize: 13,
    borderColor: THEME_COLOR4,
    color: '#000',
    borderRadius: '8px',
    '&:hover': { borderColor: '#000', backgroundColor: 'transparent' },
    '&.Mui-disabled': { borderColor: THEME_COLOR4, color: THEME_COLOR3 },
  },
};
